/* eslint-disable import/no-unresolved */
/* eslint-disable no-param-reassign */
import {
  createContext, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import API from './apiConfig';
import { toast } from 'react-toastify';
import { AuthContext } from './AuthContext';
import SessionExpired from 'src/components/auth/SessionExpired';
import PasswordExpired from 'src/components/auth/PasswordExpired';
import { format } from './tools';
import moment from 'moment';
import { useIdleTimer } from 'react-idle-timer';
import SENSOR_ICONS from './SENSOR_ICONS';
import { getPasswordExpiryDays } from './orgFeatures';

export const AppContext = createContext({});

export const useApp = () => useContext(AppContext);

const usersToSkip = ['surya@mindlabs.cloud.', 'surya@alomindlabs.com.', 'test@mindlabs.cloud.'];

const BASIC_ASSETSCONFIG = {
  asset: {
    assetName: 'Asset',
    assetGroupName: 'AssetGroup',
  },
  shipment: {
    assetName: 'Shipment',
    assetGroupName: 'ShipmentGroup',
  },
};

const AppProvider = ({ children, toggleTheme }) => {
  const {
    authStatus, sessionInfo, signOut,
  } = useContext(AuthContext);
  const [orgs, setOrgs] = useState();
  const [organization, setOrganization] = useState();
  const [showSessionExpired, setShowSessionExpired] = useState(false);
  const [showPasswordExpiryBox, setShowPasswordExpiryBox] = useState(false);
  const [username, setUsername] = useState('');
  const timeoutId = useRef();
  const [idleTimerProps, setIdleTimerProps] = useState({ disabled: true });
  const [distributorOrgId, setDistributorOrgId] = useState();

  useIdleTimer(idleTimerProps);

  const initiateSessionTimeout = (sessionMaxAge) => {
    if (timeoutId.current) clearTimeout(timeoutId.current);
    const exp = sessionInfo.idToken.payload.iat + (sessionMaxAge || 86400);
    const validForSec = exp - moment().unix();
    if (validForSec <= 0) { signOut(); return; }
    console.log('Initiating session timeout', format(exp));
    timeoutId.current = setTimeout(async () => {
      // console.log('Refreshing session...');
      // handleSessionInfo(await refreshSessionWithoutHandling(sessionInfo.refreshToken));
      setShowSessionExpired(true);
    }, validForSec * 1000);
  };

  const getUserData = async () => {
    try {
      const r = await API.get('/v1/users/self');
      if (!r.data.success) throw new Error(r.data.message);
      const passwordLastReset = Number(r?.data?.data?.passwordLastReset || 0);
      const passwordExpiryDays = getPasswordExpiryDays(organization?.id, organization?.settings);
      if (passwordExpiryDays && Date.now() - passwordLastReset >= passwordExpiryDays * 86400000) {
        setShowPasswordExpiryBox(!usersToSkip.includes(r.data.data?.username));
        setUsername(r.data.data?.username);
      }
    } catch (err) {
      toast.error(err.response?.data.message || err.response?.data || err.message);
    }
  };

  const handleLoginLogoutAudit = async (login) => {
    try {
      if (!['Stelisbiopharma01', 'stelisbiopharma'].includes(localStorage.getItem('orgId'))) {
        return;
      }
      await API.patch('v1/userlogs', {
        resource: 'mindlabs',
        action: login ? 'signin' : 'signout',
      });
    } catch (err) {
      console.log('Error:: handleLoginAudit::', err.message, err);
    }
  };

  const setupOrganization = async (org) => {
    if (!org) {
      await handleLoginLogoutAudit(false);
      localStorage.removeItem('orgId');
      setOrganization();
      return;
    }
    localStorage.setItem('orgId', org.id);
    handleLoginLogoutAudit(true);
    Object.entries(org.assetsConfig).forEach(([type, config]) => {
      config.type = type; // eslint-disable-line no-param-reassign
      if (!config.assetName) config.assetName = BASIC_ASSETSCONFIG[type].assetName; // eslint-disable-line no-param-reassign
      if (!config.assetGroupName) config.assetGroupName = BASIC_ASSETSCONFIG[type].assetGroupName; // eslint-disable-line no-param-reassign
      if (!config.otherFieldsConfig) {
        config.otherFieldsConfig = {
          note1: {
            key: 'note1',
            name: 'Note',
            type: 'text',
            lines: 4,
          },
        };
      }
    });
    org.sensorsConfig = Object.fromEntries(Object.entries(org.sensorsConfig).sort((e1, e2) => (e1[1].order < e2[1].order ? -1 : 1)));
    Object.values(org.sensorsConfig).forEach((e) => {
      if (SENSOR_ICONS[e.iconCode]) e.icon = SENSOR_ICONS[e.iconCode];
    });
    if (Object.keys(org.externalSensorsConfig || {}).length) {
      org.externalSensorsConfig = Object.fromEntries(Object.entries(org.externalSensorsConfig || {}).sort((e1, e2) => (e1[1].order < e2[1].order ? -1 : 1)));
      Object.values(org.externalSensorsConfig).forEach((e) => {
        if (SENSOR_ICONS[e.iconCode]) e.icon = SENSOR_ICONS[e.iconCode];
      });
    }
    org.settings = { loggerName: 'Logger', ...(org.settings || {}) };
    setOrganization(org);

    initiateSessionTimeout(org.sessionMaxAge);

    if (org.idleSessionTimeout) {
      if (Math.round(Date.now() / 1000) - Number(window.localStorage.lastActiveAt) > org.idleSessionTimeout) { signOut(); return; }
      console.log('Initiating idletimer', org.idleSessionTimeout);
      localStorage.setItem('lastActiveAt', Math.round(Date.now() / 1000));
      setIdleTimerProps({
        onIdle: () => { signOut(false); setShowSessionExpired(true); },
        onAction: () => localStorage.setItem('lastActiveAt', Math.round(Date.now() / 1000)),
        timeout: org.idleSessionTimeout * 1000,
        throttle: 1000,
      });
    } else setIdleTimerProps({ disabled: true });
  };

  const getOrganizations = async () => {
    try {
      const r = await API.get('/v1/organizations');
      if (r.data.success) {
        const validOrgs = r.data.data;
        // console.log(validOrgs);
        if (!validOrgs || !Object.keys(validOrgs).length) {
          toast.error('Invalid user. Please contact support');
          signOut();
          return;
        }
        setOrgs(validOrgs);
        initiateSessionTimeout();
        const distOrgId = Object.values(validOrgs).find((org) => String(org.distributorAccess) === 'true')?.id;
        if (distOrgId) setDistributorOrgId(distOrgId);
        if (validOrgs[localStorage.getItem('orgId')]) await setupOrganization(validOrgs[localStorage.getItem('orgId')]);
        else if (Object.keys(validOrgs).length === 1 && !distOrgId) await setupOrganization(Object.values(validOrgs)[0]);
      } else throw new Error(r.data.message);
    } catch (err) {
      console.log('getOrganizations::', err);
      if (err.response?.status === 401) {
        toast.error(authStatus === 'Iframe' ? 'Token expired, timeout waiting for new token from parent' : 'Session expired. Please log in again.');
        signOut();
      } else if (err.response?.status === 403) {
        toast.error('Unauthorized. Please log in again.');
        signOut();
      } else {
        toast.error(`Error getting organization info: ${err.response?.data?.message || err.response?.data || err.message}`);
      }
    }
  };

  useEffect(() => {
    if (getPasswordExpiryDays(organization?.id, organization?.settings)) {
      getUserData();
    }
  }, [organization]);

  useEffect(() => {
    if (authStatus === 'SignedIn' || authStatus === 'Iframe') getOrganizations();
    else setOrganization();
  }, [authStatus]);

  const state = useMemo(() => ({
    ...organization,
    orgs,
    setOrgs,
    setOrganization,
    setupOrganization,
    distributorOrgId,
    toggleTheme,
  }), [organization, orgs, distributorOrgId]);

  return (
    <AppContext.Provider value={state}>
      {orgs && children}
      {showSessionExpired && <SessionExpired onClick={signOut} />}
      {showPasswordExpiryBox && <PasswordExpired username={username} onClick={signOut} />}
    </AppContext.Provider>
  );
};

export default AppProvider;
